import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { 
  Key, 
  Copy, 
  Send, 
  RefreshCw, 
  Eye, 
  EyeOff, 
  CheckCircle,
  AlertCircle,
  Mail,
  Clock,
  Calendar
} from 'lucide-react';
import toast from 'react-hot-toast';
import { contractAccessService } from '../../services/contractAccessService';

const AccessCodeManagement = ({ contractId, client, onAccessGenerated }) => {
  const [showCode, setShowCode] = useState(false);
  const [accessInfo, setAccessInfo] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [includeClientEmails, setIncludeClientEmails] = useState(true);

  // Fetch existing access info on mount
  const { data: existingAccess, isLoading: loadingAccess } = useQuery(
    ['contractAccess', contractId],
    () => contractAccessService.getAccessInfo(contractId),
    {
      enabled: !!contractId,
      onSuccess: (data) => {
        if (data && data.hasAccess !== false) {
          setAccessInfo(data);
        }
      },
      onError: (error) => {
        if (error.response?.status !== 404) {
          console.error('Error loading access info:', error);
        }
      },
      retry: false
    }
  );

  // Generate access code mutation
  const generateAccessMutation = useMutation(
    async () => {
      return await contractAccessService.generateAccess(contractId, 48);
    },
    {
      onSuccess: (data) => {
        setAccessInfo(data);
        toast.success('Cod de acces generat cu succes!');
        onAccessGenerated?.(data);
      },
      onError: (error) => {
        toast.error('Eroare la generarea codului de acces');
        console.error(error);
      }
    }
  );

  // Send email mutation
  const sendEmailMutation = useMutation(
    async (emails) => {
      return await contractAccessService.sendAccessEmail(contractId, emails);
    },
    {
      onSuccess: (result) => {
        toast.success(`Email trimis cu succes către ${result.recipients} destinatar${result.recipients !== 1 ? 'i' : ''}!`);
        setShowEmailModal(false);
        setEmailAddresses('');
      },
      onError: (error) => {
        toast.error('Eroare la trimiterea emailului: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  // Get final emails helper
  const getFinalEmails = () => {
    const manualEmails = emailAddresses
      .split(/[,\s]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    const clientEmails = [];
    if (includeClientEmails && client) {
      if (client.email) clientEmails.push(client.email);
      if (client.representativeEmail) clientEmails.push(client.representativeEmail);
    }

    const allEmails = [...new Set([...manualEmails, ...clientEmails])];
    return allEmails;
  };

  // Get client emails display
  const getClientEmailsDisplay = () => {
    if (!client) return null;
    const emails = [];
    if (client.email) emails.push(client.email);
    if (client.representativeEmail) emails.push(client.representativeEmail);
    return emails.length > 0 ? emails.join(', ') : null;
  };

  const clientEmailsDisplay = getClientEmailsDisplay();

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiat în clipboard!`);
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    generateAccessMutation.mutate();
    setTimeout(() => setIsGenerating(false), 1000);
  };

  const handleSendEmail = () => {
    const emails = getFinalEmails();
    
    if (emails.length === 0) {
      toast.error('Trebuie să specificați cel puțin o adresă de email');
      return;
    }

    sendEmailMutation.mutate(emails);
  };

  const formatExpiryDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('ro-RO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Data invalidă';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Key className="w-5 h-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">
            Acces Securizat Contract
          </h3>
        </div>
      </div>

      {loadingAccess ? (
        <div className="text-center py-8">
          <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-3 animate-spin" />
          <p className="text-gray-600">Se încarcă informațiile de acces...</p>
        </div>
      ) : !accessInfo ? (
        <div className="text-center py-8">
          <div className="bg-gray-50 rounded-lg p-6 mb-4">
            <Key className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 mb-4">
              Generați un cod de acces securizat pentru acest contract
            </p>
            <p className="text-sm text-gray-500">
              Clientul va primi un link și un cod pentru a accesa contractul
            </p>
          </div>
          
          <button
            onClick={handleGenerate}
            disabled={isGenerating || generateAccessMutation.isLoading}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors inline-flex items-center"
          >
            {isGenerating || generateAccessMutation.isLoading ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Se generează...
              </>
            ) : (
              <>
                <Key className="w-5 h-5 mr-2" />
                Generează Cod de Acces
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Status Badge */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <div>
                <p className="text-sm font-medium text-green-900">
                  Cod de acces generat
                </p>
                <p className="text-xs text-green-700">
                  Codul este valid până la {formatExpiryDate(accessInfo.expiresAt)}
                </p>
              </div>
            </div>
          </div>

          {/* Access Code */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Cod de Acces (6 caractere)
              </label>
              <button
                onClick={() => setShowCode(!showCode)}
                className="text-gray-500 hover:text-gray-700"
              >
                {showCode ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-3 font-mono text-xl tracking-widest text-center">
                {showCode ? accessInfo.accessCode : '••••••'}
              </div>
              <button
                onClick={() => copyToClipboard(accessInfo.accessCode, 'Codul')}
                className="bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition-colors"
                title="Copiază codul"
              >
                <Copy className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Access Link */}
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Link de Acces
            </label>
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-600 truncate">
                {accessInfo.accessUrl}
              </div>
              <button
                onClick={() => copyToClipboard(accessInfo.accessUrl, 'Link-ul')}
                className="bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition-colors"
                title="Copiază link-ul"
              >
                <Copy className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Token Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 mb-1">
                  Cum funcționează?
                </p>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• Trimiteți link-ul de acces către client</li>
                  <li>• Comunicați codul de 6 caractere separat (de preferință pe email)</li>
                  <li>• Clientul introduce codul pentru a accesa contractul</li>
                  <li>• Codul este valid {accessInfo.validityHours || 48} ore</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-2">
            <button
              onClick={() => setShowEmailModal(true)}
              className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors inline-flex items-center justify-center"
            >
              <Mail className="w-5 h-5 mr-2" />
              Trimite pe Email
            </button>
            
            <button
              onClick={handleGenerate}
              disabled={isGenerating || generateAccessMutation.isLoading}
              className="flex-1 bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 disabled:bg-gray-300 transition-colors inline-flex items-center justify-center"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Regenerează
            </button>
          </div>

          {/* Access Stats */}
          {accessInfo.hasBeenAccessed && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-green-900">
                    Contract accesat
                  </p>
                  {accessInfo.accessedAt && (
                    <p className="text-xs text-green-700">
                      Ultima accesare: {formatExpiryDate(accessInfo.accessedAt)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowEmailModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Trimite Email de Acces</h3>
              <button onClick={() => setShowEmailModal(false)} className="text-gray-400 hover:text-gray-600">
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adrese Email Adiționale
                  <span className="block text-xs text-gray-500 font-normal mt-1">
                    Adaugă email-uri suplimentare (separați prin virgulă sau spațiu)
                  </span>
                </label>
                <textarea
                  value={emailAddresses}
                  onChange={(e) => setEmailAddresses(e.target.value)}
                  placeholder="exemplu@email.ro, alt@email.ro"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeClientEmails}
                    onChange={(e) => setIncludeClientEmails(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Include email-urile clientului
                  </span>
                </label>
                
                {includeClientEmails && clientEmailsDisplay && (
                  <div className="mt-2 flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-sm text-blue-900">{clientEmailsDisplay}</span>
                  </div>
                )}
                
                {includeClientEmails && !clientEmailsDisplay && (
                  <div className="mt-2 flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                    <span className="text-sm text-yellow-900">Clientul nu are email-uri configurate</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Anulează
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendEmailMutation.isLoading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition-colors inline-flex items-center justify-center"
              >
                {sendEmailMutation.isLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Se trimite...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Trimite Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessCodeManagement;